import React, {useContext} from "react";
import "./ArenaHighlight.css";
import StyleContext from "../../contexts/StyleContext";
import {researchinfo} from "../../portfolio";

const ARENA_URL = "https://arena-lab.org";

// Short label for a paper: the acronym before the colon, or the first words.
function shortTitle(title) {
  const head = title.split(":")[0];
  return head.length <= 28 ? head : head.split(" ").slice(0, 4).join(" ") + "…";
}

// Venue without the city, e.g. "IEEE/RSJ IROS 2026".
function shortVenue(desc) {
  return desc.split(",")[0].trim();
}

function ArenaLogo() {
  return (
    <svg className="arena-hl-logo" viewBox="0 0 40 40" fill="none" aria-hidden="true">
      <defs>
        <linearGradient id="arena-hl-grad" x1="0" y1="0" x2="40" y2="40" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="#a78bfa" />
          <stop offset="50%" stopColor="#6366f1" />
          <stop offset="100%" stopColor="#22d3ee" />
        </linearGradient>
        <linearGradient id="arena-hl-inner" x1="10" y1="8" x2="30" y2="34" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="#c4b5fd" />
          <stop offset="100%" stopColor="#67e8f9" />
        </linearGradient>
      </defs>
      <rect x="1" y="1" width="38" height="38" rx="10" stroke="url(#arena-hl-grad)" strokeWidth="2" />
      <rect x="3" y="3" width="34" height="34" rx="8" fill="rgba(12,12,20,0.85)" />
      <path d="M20 9L10 31h4l2-5h8l2 5h4L20 9zm0 7l2.5 6h-5L20 16z" fill="url(#arena-hl-inner)" />
      <circle cx="20" cy="33" r="1.5" fill="url(#arena-hl-grad)" opacity="0.6" />
    </svg>
  );
}

// Invitation to the lab website, where the newest papers, news and openings
// are kept up to date. The paper deck shows the newest entries of the
// research list below, so it follows along when papers are added.
export default function ArenaHighlight() {
  const {isDark} = useContext(StyleContext);
  const latest = researchinfo.schools.slice(0, 3);

  return (
    <section className={isDark ? "arena-hl dark" : "arena-hl"} aria-labelledby="arena-hl-title">
      <div className="arena-hl-frame">
        <div className="arena-hl-card">
          <div className="arena-hl-grid" aria-hidden="true" />
          <div className="arena-hl-orb arena-hl-orb--a" aria-hidden="true" />
          <div className="arena-hl-orb arena-hl-orb--b" aria-hidden="true" />

          <div className="arena-hl-copy">
            <div className="arena-hl-eyebrow">
              <ArenaLogo />
              <span>ARENA Lab · Singapore Management University</span>
              <span className="arena-hl-live" aria-hidden="true" />
            </div>
            <h2 id="arena-hl-title" className="arena-hl-title">
              The newest research lives at <span className="arena-hl-gradient">ARENA Lab</span>
            </h2>
            <p className="arena-hl-sub">
              Latest publications, lab news, the team and open PhD and research positions,
              all kept up to date on the lab website.
            </p>
            <div className="arena-hl-actions">
              <a className="arena-hl-cta" href={ARENA_URL} target="_blank" rel="noopener noreferrer">
                Visit ARENA Lab
                <span className="arena-hl-cta-arrow" aria-hidden="true">↗</span>
              </a>
              <a className="arena-hl-link" href={`${ARENA_URL}/publications`} target="_blank" rel="noopener noreferrer">Publications</a>
              <a className="arena-hl-link" href={`${ARENA_URL}/news`} target="_blank" rel="noopener noreferrer">News</a>
              <a className="arena-hl-link" href={`${ARENA_URL}/opportunities`} target="_blank" rel="noopener noreferrer">Join the lab</a>
            </div>
          </div>

          <a className="arena-hl-deck" href={`${ARENA_URL}/publications`} target="_blank" rel="noopener noreferrer"
            aria-label="See the latest publications on the ARENA Lab website">
            <span className="arena-hl-deck-label">Latest papers</span>
            {latest.map((paper, i) => (
              <span key={paper.schoolName} className={`arena-hl-paper arena-hl-paper--${i}`}>
                <img src={paper.logo} alt="" loading="lazy" />
                <span className="arena-hl-paper-text">
                  <span className="arena-hl-paper-venue">{shortVenue(paper.desc)}</span>
                  <span className="arena-hl-paper-title">{shortTitle(paper.schoolName)}</span>
                </span>
              </span>
            ))}
          </a>
        </div>
      </div>
    </section>
  );
}
